import { formatCodeableConcept, resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Reference,
  Resource,
  Task,
} from '@medplum/fhirtypes'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import { invariant } from '#src/invariant.ts'
import { TASK_CATEGORIES } from '#src/models/tasks/constants.ts'
import {
  SUPPORTED_TASK_INTENT,
  SUPPORTED_TASK_STATUS,
} from '#src/models/tasks/sdr/constants.ts'
import { ExtendedTask } from '#src/models/tasks/task.ts'
import { createReference } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class SDRAction extends ExtendedTask<SDRActionState> {
  get parentSdrId(): string {
    invariant(this.resource.partOf, 'SDR Action must have a partOf array')
    invariant(
      this.resource.partOf[0].reference,
      'SDR Action partOf array must have a reference'
    )
    return resolveId(this.resource.partOf[0]) as string
  }

  override mapStateToTaskFields(state: SDRActionState): {
    businessStatus: Task['businessStatus']
    intent: Task['intent']
    status: Task['status']
  } {
    return SDR_ACTION_STATE[state]
  }

  static isInstance(task: Resource): task is Task {
    return (
      isOfType(task, 'Task') &&
      hasCodeableConcept(task.code, TASK_CATEGORIES.SDR_ACTION)
    )
  }

  static buildSDRAction(
    parentTask: Task,
    params: Pick<
      Task,
      'basedOn' | 'priority' | 'owner' | 'description' | 'executionPeriod'
    >
  ): SDRAction {
    const { businessStatus, intent, status } = SDR_ACTION_STATE.CREATED

    return new SDRAction({
      partOf: [createReference(parentTask)],
      code: TASK_CATEGORIES.SDR_ACTION,
      businessStatus,
      intent,
      status,
      for: parentTask.for,
      ...params,
    })
  }

  static searchQuery(): QueryTypes {
    return {
      code: TASK_CATEGORIES.SDR_ACTION,
    }
  }

  static queryOpenBySDR(sdr: Reference<Task>): QueryTypes {
    return {
      ...this.searchQuery(),
      'part-of': sdr,
      'status:not': ['completed', 'failed', 'cancelled', 'entered-in-error'],
    }
  }
}

const SDR_ACTION_BUSINESS_STATUS = {
  CREATED: codeableConcept({
    code: 'SDR_ACTION_CREATED',
    display: 'SDR Action Created',
  }),
  IN_PROGRESS: codeableConcept({
    code: 'SDR_ACTION_IN_PROGRESS',
    display: 'SDR Action In Progress',
  }),
  COMPLETED: codeableConcept({
    code: 'SDR_ACTION_COMPLETED',
    display: 'SDR Action Completed',
  }),
  FAILED: codeableConcept({
    code: 'SDR_ACTION_FAILED',
    display: 'SDR Action Failed',
  }),
  CANCELLED: codeableConcept({
    code: 'SDR_ACTION_CANCELLED',
    display: 'SDR Action Cancelled',
  }),
}

// enforce every business status has one and only one state
export type SDRActionState = keyof typeof SDR_ACTION_BUSINESS_STATUS

export const SDR_ACTION_STATE_SELECT_OPTIONS = Object.entries(
  SDR_ACTION_BUSINESS_STATUS
).map(([key, status]) => ({
  value: key,
  label: formatCodeableConcept(status),
}))

export const SDR_ACTION_STATE = {
  CREATED: {
    businessStatus: SDR_ACTION_BUSINESS_STATUS.CREATED,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.READY,
  },
  IN_PROGRESS: {
    businessStatus: SDR_ACTION_BUSINESS_STATUS.IN_PROGRESS,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.IN_PROGRESS,
  },
  COMPLETED: {
    businessStatus: SDR_ACTION_BUSINESS_STATUS.COMPLETED,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.COMPLETED,
  },
  FAILED: {
    businessStatus: SDR_ACTION_BUSINESS_STATUS.FAILED,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.FAILED,
  },
  CANCELLED: {
    businessStatus: SDR_ACTION_BUSINESS_STATUS.CANCELLED,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.CANCELLED,
  },
} satisfies Record<
  SDRActionState,
  {
    businessStatus: CodeableConcept
    intent: (typeof SUPPORTED_TASK_INTENT)[keyof typeof SUPPORTED_TASK_INTENT]
    status: (typeof SUPPORTED_TASK_STATUS)[keyof typeof SUPPORTED_TASK_STATUS]
  }
>
